import { z } from 'zod';
import { isValidSlug, SLUG_MAX_LENGTH, SLUG_MIN_LENGTH } from './slug';

/** PRD 6.2. Only `in_development` is used until builds and reviews exist. */
export const PROJECT_STATUSES = ['in_development', 'in_review', 'live', 'suspended'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_NAME_MAX = 60;
/** Active (not archived) projects one developer can have. */
export const MAX_ACTIVE_PROJECTS = 20;

/** Addresses that would read as MOGZO's own pages rather than a game. */
export const RESERVED_SLUGS = new Set([
  'admin',
  'api',
  'apply',
  'developers',
  'games',
  'help',
  'login',
  'mogzo',
  'new',
  'official',
  'settings',
  'staff',
  'support',
  'test',
]);

export const projectNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter the game’s name')
  .max(PROJECT_NAME_MAX, `Use at most ${PROJECT_NAME_MAX} characters`);

export const projectSlugSchema = z
  .string()
  .refine(
    isValidSlug,
    `Use ${SLUG_MIN_LENGTH}–${SLUG_MAX_LENGTH} lowercase letters, numbers and single hyphens`,
  )
  .refine((slug) => !RESERVED_SLUGS.has(slug), 'That address is reserved. Try another.');

export const newProjectSchema = z.object({ name: projectNameSchema, slug: projectSlugSchema });

/** CLI sign-ins stay valid this long after their last use (and end at a password change). */
export const CLI_TOKEN_IDLE_DAYS = 90;
/** How long a `mogzo login` code can be approved. */
export const CLI_CODE_MINUTES = 10;

/** `WDJBMJHT`, `wdjb-mjht` → `WDJB-MJHT`; null if it can't be a code. */
export function normalizeUserCode(input: string): string | null {
  const letters = input.toUpperCase().replace(/[^A-Z]/g, '');
  return letters.length === 8 ? `${letters.slice(0, 4)}-${letters.slice(4)}` : null;
}

export const BUILD_STATUSES = ['uploading', 'processing', 'ready', 'failed'] as const;
export type BuildStatus = (typeof BUILD_STATUSES)[number];

const MB = 1024 * 1024;
/** Limits for one uploaded build (the PRD leaves quota values to Phase 1; these are a start). */
export const BUILD_LIMITS = {
  zipBytes: 100 * MB,
  unpackedBytes: 250 * MB,
  files: 2000,
  /** Builds a project can have waiting to upload or being processed at once. */
  inFlight: 3,
  /**
   * The CLI warns when a splash page and the files it names come to more than this: splashes
   * run in feeds, several at a time, so they should load fast.
   */
  splashBytes: 2 * MB,
} as const;
export const BUILD_NOTES_MAX = 500;
